import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Rating,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import type { CrewMember } from "./site-visit.types";
import { tokens } from "../../../../theme";

interface CrewMemberCardProps {
  crewMember: CrewMember;
  isSelected: boolean;
  onSelect: (crewMember: CrewMember) => void;
  onDeselect: (crewMember: CrewMember) => void;
}

const CrewMemberCard: React.FC<CrewMemberCardProps> = ({
  crewMember,
  isSelected,
  onSelect,
  onDeselect,
}) => {
  const { user } = crewMember;

  const fullName = `${user.first_name} ${user.last_name}`;
  const phoneNumber = user.individual?.phone_number;
  const email = user.email;
  const rating = crewMember.average_rating;

  const theme = useTheme();
  const colors = useMemo(
    () => tokens(theme.palette.mode),
    [theme.palette.mode],
  );

  const handleClick = () => {
    if (isSelected) {
      onDeselect(crewMember);
    } else {
      onSelect(crewMember);
    }
  };

  const handlePhoneClick = () => {
    if (phoneNumber) window.location.href = `tel:${phoneNumber}`;
  };

  const handleEmailClick = () => {
    if (email) window.location.href = `mailto:${email}`;
  };

  return (
    <Card
      sx={{
        boxShadow: 0,
        borderRadius: "16px",
        bgcolor: colors.primary[800],
        transition: "all 0.2s ease-in-out",
        overflow: "hidden",
        "&:hover": {
          boxShadow: 3,
          transform: "translateY(-1px)",
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          textAlign: "center",
          "&:last-child": { pb: 2 },
        }}
      >
        {/* Avatar */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <Avatar
            src={user.profile_picture || undefined}
            alt={fullName}
            sx={{
              width: 64,
              height: 64,
              fontSize: 20,
            }}
          >
            {user.first_name.charAt(0)}
            {user.last_name.charAt(0)}
          </Avatar>

          {isSelected && (
            <CheckCircleIcon
              color="success"
              sx={{
                position: "absolute",
                bottom: 0,
                right: "calc(50% - 38px)",
                bgcolor: "white",
                borderRadius: "50%",
                fontSize: 22,
              }}
            />
          )}
        </Box>

        {/* Name */}
        <Typography
          variant="h6"
          fontWeight={500}
          sx={{
            mb: 1,
            fontSize: "1rem",
            lineHeight: 1.2,
          }}
        >
          {fullName}
        </Typography>

        {/* Rating */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <Rating value={rating} precision={0.5} readOnly size="small" />
        </Box>

        {/* Availability */}
        <Box
          sx={{
            display: "inline-flex",
            px: 2,
            py: 0.5,
            borderRadius: "999px",
            bgcolor: "rgba(255,255,255,0.5)",
            mb: 2,
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            Available
          </Typography>
        </Box>

        {/* Contact Icons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Tooltip
            title={phoneNumber ? `Call ${phoneNumber}` : "Phone not available"}
          >
            <span>
              <IconButton
                onClick={handlePhoneClick}
                disabled={!phoneNumber}
                sx={{
                  opacity: phoneNumber ? 1 : 0.4,
                }}
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={email ? `Email ${email}` : "Email not available"}>
            <span>
              <IconButton
                onClick={handleEmailClick}
                disabled={!email}
                sx={{
                  opacity: email ? 1 : 0.4,
                }}
              >
                <EmailIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Assign Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleClick}
          sx={{
            borderRadius: "10px",
            py: 1,
            fontSize: "0.85rem",
            fontWeight: 500,
            bgcolor: colors.primary[400],
            textTransform: "uppercase",
            boxShadow: 2,
          }}
        >
          {isSelected ? "Assigned" : "Assign"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default CrewMemberCard;
